import {
  Alert,
  Box,
  Button,
  Card,
  Flex,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE, MIME_TYPES, MS_WORD_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDownload, IconPhoto, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { AutoBrandData, AutoBrandTitlePageMode } from 'src/secret-menu/auto-brand/types';
import { useDownloadTitlePageTemplate } from 'src/secret-menu/export-assets/hooks/useExportBranding';

function TitlePageNoneMode() {
  return (
    <Alert color="gray" variant="light">
      The tenant will use the default title page behaviour. You can always come back and configure
      one later via the 3-dot menu on the auto-brand tab.
    </Alert>
  );
}

function TitlePageTemplateMode({
  companyId,
  titlePageDocx,
  onDocxUpload,
}: {
  companyId: string;
  titlePageDocx: File | null;
  onDocxUpload: (files: File[]) => void;
}) {
  const { downloadTemplate, isLoading: isDownloading } = useDownloadTitlePageTemplate(companyId);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      await downloadTemplate();
    } catch (err) {
      notifications.show({
        title: 'Download failed',
        message: err instanceof Error ? err.message : 'Could not generate title page template.',
        color: 'red',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack gap="md">
      <Card withBorder radius="md" p="lg">
        <Flex gap="md" align="flex-start">
          <ThemeIcon variant="light" color="blue" size={40} radius="xl">
            <IconDownload size={20} />
          </ThemeIcon>
          <Stack gap="xs" flex={1}>
            <Text fw={FONT_WEIGHTS.semibold}>1. Download the styled template</Text>
            <Text size="textSm" c="gray.7">
              Generate an A4 DOCX template with all the styles pre-defined. Add your title page
              imagery in Word, then upload it below.
            </Text>
            <Box>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadTemplate}
                loading={downloading || isDownloading}
              >
                Download template
              </Button>
            </Box>
          </Stack>
        </Flex>
      </Card>

      <Card withBorder radius="md" p="lg">
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>2. Upload the final title page</Text>
          <Dropzone
            onDrop={onDocxUpload}
            maxFiles={1}
            accept={[...MS_WORD_MIME_TYPE, MIME_TYPES.docx]}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 8,
              padding: 24,
            }}
          >
            <Flex align="center" justify="center" gap="sm">
              <IconUpload size={20} />
              <Text size="textSm">
                {titlePageDocx ? titlePageDocx.name : 'Drop the finished title page .docx here'}
              </Text>
            </Flex>
          </Dropzone>
        </Stack>
      </Card>
    </Stack>
  );
}

function TitlePageImageOverlayMode({
  titlePageImage,
  onImageDrop,
}: {
  titlePageImage: File | null;
  onImageDrop: (files: File[]) => void;
}) {
  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="sm">
        <Flex gap="md" align="flex-start">
          <ThemeIcon variant="light" color="blue" size={40} radius="xl">
            <IconPhoto size={20} />
          </ThemeIcon>
          <Stack gap="xs" flex={1}>
            <Text fw={FONT_WEIGHTS.semibold}>Upload a cover image</Text>
            <Text size="textSm" c="gray.7">
              When you save, we&apos;ll regenerate the export assets with your new branding, then
              fit this image to A4 and place it behind text as a full cover overlay.
            </Text>
          </Stack>
        </Flex>

        <Dropzone
          onDrop={onImageDrop}
          maxFiles={1}
          accept={IMAGE_MIME_TYPE}
          style={{
            border: '1px dashed var(--mantine-color-gray-4)',
            borderRadius: 8,
            padding: 24,
          }}
        >
          <Flex align="center" justify="center" gap="sm">
            <IconPhoto size={20} />
            <Text size="textSm">
              {titlePageImage ? titlePageImage.name : 'Drop a PNG or JPG image here'}
            </Text>
          </Flex>
        </Dropzone>
      </Stack>
    </Card>
  );
}

export default function TitlePageStep(props: StepProps<AutoBrandData>) {
  const { data, setData, nextStep, previousStep } = props;
  const companyId = data.company?.id ?? '';

  const mode = data.titlePageMode;
  const setMode = (next: AutoBrandTitlePageMode) => {
    setData(prev => ({
      ...prev,
      titlePageMode: next,
      titlePageDocx: null,
      titlePageImage: null,
      titlePagePreparedLocation: null,
    }));
  };

  const handleDocxUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setData(prev => ({ ...prev, titlePageDocx: file }));
  };

  const handleImageDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setData(prev => ({ ...prev, titlePageImage: file, titlePagePreparedLocation: null }));
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Configure a title page (optional)"
      description="Skip this step if the tenant doesn't need a title page, or pick one of the two modes below."
      continueText="Continue"
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      previousStep={() => previousStep()}
      nextStep={() => nextStep()}
    >
      <Stack gap="lg">
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="Destructive action"
        >
          Saving this flow will permanently overwrite the tenant&apos;s existing export assets and
          title page. This cannot be undone. Make sure your branding configuration is correct before
          proceeding.
        </Alert>

        <SegmentedControl
          fullWidth
          value={mode}
          onChange={value => setMode(value as AutoBrandTitlePageMode)}
          data={[
            { value: 'none', label: 'Skip' },
            { value: 'template_upload', label: 'Template + image' },
            { value: 'image_overlay', label: 'Full image overlay' },
          ]}
        />

        {mode === 'none' && <TitlePageNoneMode />}

        {mode === 'template_upload' && (
          <TitlePageTemplateMode
            companyId={companyId}
            titlePageDocx={data.titlePageDocx}
            onDocxUpload={handleDocxUpload}
          />
        )}

        {mode === 'image_overlay' && (
          <TitlePageImageOverlayMode
            titlePageImage={data.titlePageImage}
            onImageDrop={handleImageDrop}
          />
        )}
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
